<template lang="pug">
router-link.product-teaser-link(
    :to="route",
    @mouseover.native="hover",
    @mouseleave.native="blur"
)
    .product-teaser(:style="style", ref="container")
        .card-teaser.grid(
            :class="{ hover: mouseover }"
        )
            img.sale-image(v-if="sale" title="sale" src="~@/assets/img/sale.png")
            figure.overflow-hidden(
                v-if="image && error === false"
            )
                lazy-image(
                    :src="image",
                    :title="teaser.name",
                    :width="image.width",
                    :height="image.height",
                    :class="{ loaded }",
                    @loaded="imageLoadingSucceed",
                    @error="imageLoadingFailed"
                )
            //- TODO: video
            .placeholder(v-else)
                svg(v-if="!image || loading === false")
                    line(x1="0", y1="100%", x2="100%", y2="0")
                    line(x1="0", y1="0", x2="100%", y2="100%")
            .info-container.flex.relative
                .info.flex.flex-col.py-2
                    .name {{ teaser.name }}
                    .price.text-left
                        //- nobr.font-black {{ price }}
                        nobr.ek-price(:class="{ strikethrough: salePrice }") EK {{ price }}
                        nobr.uvp-price(:class="{ strikethrough: salePrice }") UVP {{ uvp }}
                        nobr.sale(v-if="salePrice") {{ salePrice }}
</template>

<script>
import uniqid from 'uniqid';
import { mapGetters, mapActions } from 'vuex';
import striptags from 'striptags';
import { get, trim, debounce } from '@/helpers';
import { moneyFormat } from '@/filters';

export default {
    name: 'Teaser',
    data() {
        return {
            delay: 150,
            mouseover: false,
            loaded: false,
            loading: true,
            fetching: false,
            error: false,
            preferences: {
                width: 300,
                height: 450
            }
            // stars: 5,
        };
    },
    props: {
        teaser: {
            type: Object,
            required: true
        }
    },
    watch: {
        display() {
            this.$nextTick(this.updatePreferences);
        }
    },
    computed: {
        route() {
            return {
                name: 'product-details',
                params: {
                    product: this.teaser.slug
                }
            };
        },
        sale() {
            const propertyPivots = this.teaser.propertyPivots || [];
            return propertyPivots.find(
                el => el.property.key === 'sale' && el.values.length === 1 && el.values[0].number === 1
            );
        },
        uvp() {
            let price = this.teaser.mainPrice;
            if (!price) return null;

            const customerGroup = price.customerGroup || get(this.cms, 'shop.defaultCustomerGroup');
            const code = get(customerGroup, 'currency.iso4217') || 'EUR';
            let formattedPrice = moneyFormat(price.value, code);
            // if(this.customer && this.customer.show_net_prices) formattedPrice = moneyFormat(price.net_value, code);

            return formattedPrice;
        },
        price() {
            const price = this.teaser.price;
            if (!price) return null;

            const customerGroup = price.customerGroup || get(this.cms, 'shop.defaultCustomerGroup');
            const code = get(customerGroup, 'currency.iso4217') || 'EUR';

            let value = price.value;
            if (this.customer && get(this.customer, 'show_net_prices')) value = price.net_value;

            return moneyFormat(value, code);
        },
        salePrice() {
            if (!this.teaser.salePrice) return null;

            const price = this.teaser.salePrice;
            const customerGroup = price.customerGroup || get(this.cms, 'shop.defaultCustomerGroup');
            const code = get(customerGroup, 'currency.iso4217') || 'EUR';

            let value = price.value;
            if (this.customer && get(this.customer, 'show_net_prices')) value = price.net_value;

            return moneyFormat(value, code);
        },
        image() {
            let image;

            if (
                this.teaser.media &&
                this.teaser.media.type === 'image' &&
                typeof this.teaser.media.image === 'object'
            ) {
                let data = this.teaser.media.image;

                const storageRoute = trim(
                    `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                    '/'
                );

                let { method } = get(data, 'settings') || {};
                const { width, height } = data;
                let aspectRatio = width / height;

                let defaultWidth = get(this.teaser, 'style.width', this.preferences.width);
                let defaultHeight = get(this.teaser, 'style.height')
                    ? this.teaser.style.height - 70
                    : this.preferences.height;

                if (this.display === 'row') {
                    defaultHeight = defaultWidth = 180;
                }

                let w = Math.min(width, defaultWidth + 1);
                let h = Math.min(height, defaultHeight + 1);

                if (aspectRatio > 1) {
                    // landscape
                    // limit by width
                    h = w / aspectRatio;
                } else if (aspectRatio < 1) {
                    // portrait
                    // limit by height
                    w = h * aspectRatio;
                }

                let fit = `fit&w=${w}&h=${h}`;

                image = {
                    src: `${storageRoute}/images/${data.id}/${data.name}?${fit}`,
                    error: `${storageRoute}/images/${data.id}/${data.name}?${fit}&preview=greyscale`,
                    loading: `${storageRoute}/images/${data.id}/${data.name}?fit&w=200&h=200&preview=blur`,
                    width: w,
                    height: h,
                    aspectRatio
                };
            }

            return image;
        },
        style() {
            let style;
            if (this.display === 'tile' && this.teaser.style) {
                style = {
                    position: 'absolute',
                    display: 'flex',
                    left: `${this.teaser.style.left}px`,
                    top: `${this.teaser.style.top}px`,
                    width: `${this.teaser.style.width}px`
                    // height: `${this.teaser.style.height}px`
                };
            }
            return style;
        },
        clamp() {
            let clamp = 3;
            if (this.ratings && this.ratings.length) clamp = 2;
            return clamp;
        },
        ...mapGetters({
            cms: 'CMS',
            customer: 'shop/customer'
        })
    },
    methods: {
        hover() {
            this.$emit('focus', this.item);
            this.mouseover = true;
        },
        blur() {
            setTimeout(() => {
                this.$emit('blur', this.item);
                this.mouseover = false;
            }, this.delay);
        },
        striptags(html) {
            html = html.replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '<br />');
            return striptags(html, ['a', 'i', 'u', 'b', 'em', 'strong', 'span', 'br']);
        },
        imageLoadingSucceed() {
            this.loading = false;
            this.loaded = true;
        },
        imageLoadingFailed() {
            this.error = true;
            this.loading = false;
        },
        updatePreferences() {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container);
                this.preferences.width = this.$refs.container.clientWidth;
                this.preferences.width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);

                // make it square
                // this.preferences.height = this.preferences.width;
            }
        },
        ...mapActions({
            addMessage: 'message/add',
            fetchProduct: 'shop/fetchProduct'
        })
    },
    mounted() {
        window.addEventListener('resize', debounce(this.updatePreferences, 100));
        this.$nextTick(this.updatePreferences);
    }
};
</script>
<style lang="sass" nolang="scss" scoped>
@import "~@/assets/sass/variables"

a.product-teaser-link
  text-decoration: none
.to-article-box
    @apply py-2 px-1 bg-gray-500 text-white text-center mt-4
    width: 150px
    display: inline-block
    &:hover
        @apply bg-gray-700

.card-teaser,
.tile-teaser,
.row-teaser
  background-color: $white
  width: 100%
  transition: transform .3s ease
  &:after
    @apply rounded
    content: ''
    position: absolute
    top: 0
    left: 0
    height: 100%
    width: 100%
    // box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)
    z-index: -1
    transform: translateZ(-50px)
    transition: .3s
  .placeholder
    // padding-bottom: 100%
    padding-bottom: calc(100% * 610/422)
    position: relative
    svg
      width: 100%
      height: 100%
      position: absolute
      top: 0
      left: 0
      line
        stroke: rgba($red, .3)
        stroke-width: 1.02
  figure
    display: flex
    width: 100%
    justify-content: center
    align-items: center
    // height: 300px
    img
      max-width: 100%
      // max-height: 100%
      display: block !important
      opacity: 0
      transition: opacity .3s ease
      &.loaded
        opacity: 1
  .label-image
    @apply absolute z-50
    top: 11px
    right: 11px
    max-width: 30px
    @screen sm
        max-width: 50px
  .info
    // height: 135px
    overflow-x: hidden
    width: 100%
    .name, .description
      line-height: 1.3
      word-break: normal !important
  .description
    font-size: .85rem
    @screen sm
      font-size: 1rem
  .price
    @apply mt-1
    @screen sm
      @apply mt-3
  .text-lg
    font-size: 1.15rem
    @screen sm
      @apply text-lg

.card-teaser
  .description,
  .properties
    margin-top: 0.5rem
  .price
    display: grid
    padding-top: 0.5rem
    margin-top: auto
    .ek-price
        font-weight: 600
    .uvp-price
        font-weight: 400
  figure
    position: relative
    border: 1px solid $color-primary
    padding: 10px
    img
      z-index: 1
    .background
      opacity: 0
      z-index: 0
      background-size: cover
      // background-attachment: fixed
      position: absolute
      left: 0
      top: 0
      width: 100%
      height: 100%
      transition: opacity .1s ease
    img.loaded + .background
      opacity: 1
.font-black
    @apply text-black font-normal
.tile-teaser
  .to-article-box
    width: 100%
  .info-container
    background-color: $white
    width: 100%
    height: auto
    position: absolute
    bottom: 0
    .properties
      margin-top: 0.5rem
    .content
      margin-top: 0.5rem
      &::v-deep
        br + br
          display: none

.card-teaser, .tile-teaser
  ::v-deep .property
    @apply mb-1
    .values
      margin-left: -2px
      height: 22px
      overflow-y: hidden
      overflow-x: scroll
      scrollbar-width: none
      -ms-overflow-style: none
      &::-webkit-scrollbar
        display: none

.row-teaser
  display: grid
  margin-bottom: 1rem
  background-color: transparent
  transition: transform .3s ease, background-color .3s ease
  grid-template-columns: 80px auto
  @screen sm
    grid-template-columns: 140px auto
  @screen md
    grid-template-columns: calc(180px + 2rem) auto
  &:after
    box-shadow: none
  .placeholder
    padding-bottom: 100%
    position: relative
    svg
      width: calc(100% - 2rem)
      height: calc(100% - 2rem)
      position: absolute
      top: 1rem
      left: 1rem
  figure
    padding: 0
    width: 80px
    height: auto
    @screen sm
      width: 140px
    @screen md
      min-height: calc(180px + 2rem)
      width: calc(180px + 2rem)
      padding: 1rem
    img
      height: auto !important
  .info
    display: flex
    flex-direction: column
    justify-content: space-between
    @apply px-2 py-1
    @screen sm
      @apply px-4 py-3
    @screen md
      @apply px-4 py-6
  ::v-deep .property
    @apply mb-3

.product-teaser
  // transform-style: preserve-3d
  will-change: transform
  // .name, .description, .price
  //   transform: translateZ(50px) scale(1)

.card-teaser, .tile-teaser
  .details-button,
  .add-to-cart
    position: absolute
    right: 0.5rem
    top: -1.8rem
    z-index: 1
    transform: scale(0.8)
    transition: opacity .15s ease, transform .15s ease

    @screen md
      right: 0.8rem
      opacity: 0
    button
      padding-left: 0.4rem
      padding-right: 0.4rem
      min-width: 40px
      height: 40px
      background-color: #000
      border-color: #000
      svg
        width: 1.125rem


a:hover
  z-index: 2
  position: relative
  .details-button,
  .add-to-cart
    opacity: 1
    transform: scale(1)
  .tile-teaser, .card-teaser, .row-teaser
    &:after
      //box-shadow: 0 10px 50px -10px rgba(0, 0, 0, 0.2), 0 40px 80px 0 rgba(0, 0, 0, 0.05)
  .tile-teaser, .card-teaser
    //background-color: blue
  .row-teaser
    //transform: scale(1.01)
    background-color: theme('colors.gray.50')

// all cards same height
.horizontal .product-teaser
  min-height: 100%
  display: flex
  .card-teaser
    grid-template-rows: max-content auto

.strikethrough
  font-size: 0.8em
  opacity: 0.6
  color: $black
  &:before
    border-color: theme('colors.gray.700')

.price
  position: relative
.price nobr + nobr.sale
  margin-left: 1rem
  color: theme('colors.gray.700')
  position: relative
  z-index: 1
  &:before
    content: ''
    position: absolute
    // background-color: theme('colors.red.100')
    background-color: #e6ff15
    width: calc(100% + 8px)
    height: calc(100% + 4px)
    left: -4px
    top: -7px
    z-index: -1
    transform: rotate(-9deg) skew(-2deg, 0deg)
.info.flex.flex-col.py-2
    .name
        color: $color-primary
    .nr
        color: $color-primary
        font-weight: 600
.ek-price
        color: $color-primary
.uvp-price
        color: $color-primary

.sale-image
    position: absolute
    top: 11px
    right: 11px
    z-index: 2
</style>

    